import type { OrganizationActivityStatsQuery } from "@/graphql/userAction.generated";
import type { OrganizationLmsStatsQuery } from "@/graphql/assignments.generated";
import type { OrganizationMembershipStatsQuery } from "@/graphql/organizations.generated";
import type { TimeFrame } from "@/components/TimeFrameSelector";
import {
    daysInMonth,
    engagementCategoryLabelMap,
    formatDayLabel,
    formatMonthLabel,
    frequencyTierLabelMap,
    hasAnyValue,
    pct,
    clientPlatforms,
    platformLabel,
    topVideoTags,
} from "./statsHelpers";

type Stats = OrganizationActivityStatsQuery["organizationActivityStats"];
type LmsStats = OrganizationLmsStatsQuery["organizationLmsStats"];
type MembershipStats = OrganizationMembershipStatsQuery["organizationMembershipStats"];

export const STATS_CSV_HEADERS = [
    "Section",
    "Metric",
    "Value",
];

const NO_DATA = "No data";
const DASH = "-";

export interface BuildStatsCsvRowsInput {
    stats: Stats;
    lmsStats: LmsStats | null | undefined;
    membershipStats: MembershipStats | null | undefined;
    timeFrame: TimeFrame;
    /** False drops the view counts from Top Video Tags, leaving each tag ranked by views. */
    includeTagViews?: boolean;
    /** False drops the view and unique-viewer counts from Top Video Topics, leaving each title ranked by views. */
    includeTopicViews?: boolean;
}

/** One row per numeric field the stats queries return, or a placeholder row when a section has nothing. */
export const buildStatsCsvRows = ({
    stats,
    lmsStats,
    membershipStats,
    timeFrame,
    includeTagViews = true,
    includeTopicViews = true,
}: BuildStatsCsvRowsInput): string[][] => {
    const rows: string[][] = [];
    const row = (section: string, metric: string, value: string | number) => {
        rows.push([
            section,
            metric,
            String(value),
        ]);
    };

    // Total, then its two halves, then the rate over the total — the same denominator the page
    // shows, so the export and the page cannot disagree about what a percentage is of.
    const roster = stats.totalMembers + stats.invitedUsers;
    row("Users & Actions", "Total users", roster);
    row("Users & Actions", "Users with accounts", stats.totalMembers);
    row("Users & Actions", "Invited users", stats.invitedUsers);
    row("Users & Actions", "Active users", stats.activeUsers);
    row("Users & Actions", "Active user rate", roster ? pct(stats.activeUsers / roster) : DASH);
    row("Users & Actions", "Total actions", stats.totalActions);
    row(
        "Users & Actions",
        "Avg actions per user",
        stats.activeUsers ? (stats.totalActions / stats.activeUsers).toFixed(1) : DASH,
    );

    row("Email Newsletter", "Email reach rate", pct(stats.emailReachRate));
    // The counts behind the rate, for the same reason the page shows them — see ofMembers.
    row("Email Newsletter", "Email openers", stats.emailOpeners);
    row("Email Newsletter", "Email click rate", pct(stats.emailClickRate));
    row("Email Newsletter", "Email clickers", stats.emailClickers);

    row("Coaching", "Coaching users", stats.coachingUsers);
    row("Coaching", "Coaching sessions", stats.coachingSessions);
    row("Coaching", "Coaching messages", stats.coachingMessages);
    row("Coaching", "Messages to coach", stats.messagesToCoach);

    if (lmsStats) {
        row("Learning Management System", "Users in progress", lmsStats.usersWithActiveAssignments);
        row("Learning Management System", "Users who started a course", lmsStats.usersWhoStartedCourses);
        row("Learning Management System", "Users who completed a course", lmsStats.usersWhoCompletedCourses);
    } else {
        row("Learning Management System", DASH, NO_DATA);
    }

    if (stats.videoViewSummaries.length === 0) {
        row("Top Video Tags", DASH, DASH);
        row("Top Video Topics", DASH, DASH);
    } else {
        const tags = topVideoTags(stats.videoViewSummaries);
        const videos = [...stats.videoViewSummaries].sort((a, b) => b.views - a.views).slice(0, 5);
        if (includeTagViews) {
            for (const { tag, views } of tags) row("Top Video Tags", tag, views);
        } else {
            tags.forEach(({ tag }, i) => row("Top Video Tags", `#${i + 1}`, tag));
        }
        if (includeTopicViews) {
            for (const video of videos) {
                row("Top Video Topics", `${video.title} — views`, video.views);
                row("Top Video Topics", `${video.title} — unique viewers`, video.uniqueViewers);
            }
        } else {
            videos.forEach((video, i) => row("Top Video Topics", `#${i + 1}`, video.title));
        }
    }

    // Same filtered rows the charts draw, so the export and the page cannot disagree.
    const byPlatform = clientPlatforms(stats.byPlatform);
    if (hasAnyValue(byPlatform, (r) => r.total)) {
        for (const r of byPlatform) row("Platforms - Actions", platformLabel(r.platform), r.total);
    } else {
        row("Platforms - Actions", DASH, NO_DATA);
    }

    const usersByPlatform = clientPlatforms(stats.usersByPlatform);
    if (hasAnyValue(usersByPlatform, (r) => r.total)) {
        for (const r of usersByPlatform) row("Platforms - Users", platformLabel(r.platform), r.total);
    } else {
        row("Platforms - Users", DASH, NO_DATA);
    }

    if (hasAnyValue(stats.frequencyTiers, (r) => r.userCount)) {
        for (const r of stats.frequencyTiers) {
            row("Frequency Tiers", frequencyTierLabelMap[r.tier] ?? r.tier, r.userCount);
        }
    } else {
        row("Frequency Tiers", DASH, NO_DATA);
    }

    if (hasAnyValue(stats.byEngagementCategory, (r) => r.total)) {
        for (const r of stats.byEngagementCategory) {
            row("Engagement Buckets", engagementCategoryLabelMap[r.category] ?? r.category, r.total);
        }
    } else {
        row("Engagement Buckets", DASH, NO_DATA);
    }

    if (hasAnyValue(stats.byMonth, (r) => r.totalActions)) {
        for (const r of stats.byMonth) row("Actions per Month", formatMonthLabel(r.month), r.totalActions);
    } else {
        row("Actions per Month", DASH, NO_DATA);
    }

    if (hasAnyValue(stats.byMonth, (r) => r.totalActions)) {
        for (const r of stats.byMonth) {
            const monthLabel = formatMonthLabel(r.month);
            row("Actions per Month by User", `${monthLabel} — total actions`, r.totalActions);
            row("Actions per Month by User", `${monthLabel} — active users`, r.activeUsers);
        }
    } else {
        row("Actions per Month by User", DASH, NO_DATA);
    }

    // Only ever shown for a MONTH-mode time frame — matches the chart it exports alongside.
    if (timeFrame.mode === "MONTH") {
        const totalByDay = new Map(
            stats.byDay.map((r) => [
                r.day,
                r.total,
            ]),
        );
        const dailyData = daysInMonth(timeFrame.from.getFullYear(), timeFrame.from.getMonth()).map((day) => ({
            day,
            total: totalByDay.get(day) ?? 0,
        }));
        if (hasAnyValue(dailyData, (r) => r.total)) {
            for (const r of dailyData) row("Daily Actions", formatDayLabel(r.day), r.total);
        } else {
            row("Daily Actions", DASH, NO_DATA);
        }
    }

    if (membershipStats && hasAnyValue(membershipStats, (r) => r.total)) {
        for (const r of membershipStats) row("User Growth", formatMonthLabel(r.month), r.total);
    } else {
        row("User Growth", DASH, NO_DATA);
    }

    return rows;
};
